import { Fan, Pulse, Question, ThermometerSimple } from "@phosphor-icons/react";

import type { Device } from "@/lib/api";
import { cn } from "@/lib/utils";

import { InlineEdit } from "./inline-edit";
import { MissingChip } from "./missing-chip";

const DEVICE_ICONS: Record<string, typeof Pulse> = {
  AirthingsSensor: Pulse,
  Heating: ThermometerSimple,
  Ventilation: Fan,
};

export function DeviceIcon({ deviceType }: { deviceType: string }) {
  const Icon = DEVICE_ICONS[deviceType] ?? Question;
  return (
    <span className="bg-surface-subtle text-muted grid size-[30px] shrink-0 place-items-center rounded-lg">
      <Icon className="size-4" weight="light" />
    </span>
  );
}

interface DeviceRowProps {
  device: Device;
  onChange: (patch: Partial<Device>) => Promise<void>;
  /** Hide the outer horizontal padding when nested in a card that already pads. */
  bleed?: boolean;
  /** Pre-provisioned AirThings sensor serials, offered as autocomplete for `external_id`. */
  airthingsSensorIds?: string[];
}

/** A single device line: icon, name/brand/type/link-id, and a linked-status pill. */
export function DeviceRow({ device, onChange, bleed = false, airthingsSensorIds }: DeviceRowProps) {
  const linkId = device.ha_device_id || device.external_id || "";
  const isLinked = Boolean(linkId);

  return (
    <div
      className={cn(
        "border-border-subtle flex items-center gap-2.5 border-t py-2 text-[13.5px] first:border-t-0",
        bleed ? "px-0" : "px-5",
      )}
    >
      <DeviceIcon deviceType={device.device_type} />
      <div className="min-w-0 grow">
        <div className="text-primary font-semibold">
          <InlineEdit
            value={device.name}
            onSave={(v) => onChange({ name: v })}
            ariaLabel="Device name"
          >
            {device.name}
          </InlineEdit>
        </div>
        <div className="text-muted flex flex-wrap items-center gap-1 font-mono text-[11.5px]">
          <InlineEdit value={device.brand} onSave={(v) => onChange({ brand: v })} ariaLabel="Brand">
            {device.brand}
          </InlineEdit>
          <span>·</span>
          <InlineEdit
            value={device.device_type}
            onSave={(v) => onChange({ device_type: v })}
            ariaLabel="Device type"
          >
            {device.device_type}
          </InlineEdit>
          <span>·</span>
          <InlineEdit
            value={device.ha_device_id ?? ""}
            onSave={(v) => onChange({ ha_device_id: v || null })}
            ariaLabel="Home Assistant device id"
            placeholder="unlinked"
          >
            {linkId || <span className="text-warning">unlinked</span>}
          </InlineEdit>
        </div>
        {device.device_type === "AirthingsSensor" && (
          <div className="text-muted flex items-center gap-1 font-mono text-[11.5px]">
            <span>serial:</span>
            <InlineEdit
              value={device.external_id ?? ""}
              onSave={(v) => onChange({ external_id: v || null })}
              ariaLabel="AirThings sensor serial"
              placeholder="sensor serial"
              listOptions={airthingsSensorIds}
            >
              {device.external_id || <span className="text-muted">unset</span>}
            </InlineEdit>
          </div>
        )}
      </div>
      {!device.external_id && !device.ha_device_id && <MissingChip variant="handoff" />}
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-sm px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap before:size-1.5 before:rounded-full before:bg-current",
          isLinked ? "bg-success/10 text-success" : "bg-warning/10 text-warning",
        )}
      >
        {isLinked ? "Linked" : "Needs attention"}
      </span>
    </div>
  );
}

interface RoomDevicesProps {
  devices: Device[];
  onChange: (deviceIndex: number, patch: Partial<Device>) => Promise<void>;
  /** Pre-provisioned AirThings sensor serials, offered as autocomplete for `external_id`. */
  airthingsSensorIds?: string[];
}

export function RoomDevices({ devices, onChange, airthingsSensorIds }: RoomDevicesProps) {
  if (devices.length === 0) {
    return null;
  }
  return (
    <div>
      <div className="text-muted type-label px-5 pt-3 pb-1">Devices · {devices.length}</div>
      {devices.map((device, i) => (
        <DeviceRow
          key={i}
          device={device}
          onChange={(patch) => onChange(i, patch)}
          airthingsSensorIds={airthingsSensorIds}
        />
      ))}
    </div>
  );
}
